import { Check, X } from 'lucide-react';
import { PasswordStrength } from '@/hooks/usePasswordValidation';
import { cn } from '@/lib/utils';

interface PasswordStrengthIndicatorProps {
  strength: PasswordStrength;
  showRequirements?: boolean;
}

const PasswordStrengthIndicator = ({ 
  strength, 
  showRequirements = true 
}: PasswordStrengthIndicatorProps) => {
  if (!strength.checks.minLength && strength.feedback.length === 6) {
    // Password is empty or too short to evaluate
    return null;
  }

  return (
    <div className="space-y-2 mt-2">
      {/* Strength bar */}
      <div className="flex gap-1">
        {[0, 1, 2, 3, 4].map((index) => (
          <div
            key={index}
            className={cn(
              'h-1.5 flex-1 rounded-full transition-colors duration-200',
              index <= strength.score ? strength.color : 'bg-muted'
            )}
          />
        ))}
      </div>
      
      {/* Strength label */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          Password strength: <span className={cn(
            'font-medium',
            strength.score === 0 && 'text-destructive',
            strength.score === 1 && 'text-orange-500',
            strength.score === 2 && 'text-yellow-600',
            strength.score >= 3 && 'text-green-600'
          )}>{strength.label}</span>
        </span>
      </div>

      {/* Requirements checklist */}
      {showRequirements && strength.feedback.length > 0 && (
        <div className="grid grid-cols-2 gap-1 text-xs">
          <RequirementItem met={strength.checks.minLength} text="8+ characters" />
          <RequirementItem met={strength.checks.hasUppercase} text="Uppercase" />
          <RequirementItem met={strength.checks.hasLowercase} text="Lowercase" />
          <RequirementItem met={strength.checks.hasNumber} text="Number" />
          <RequirementItem met={strength.checks.hasSpecial} text="Special char" />
          <RequirementItem met={strength.checks.notCommon} text="Not common" />
        </div>
      )}
    </div>
  );
};

const RequirementItem = ({ met, text }: { met: boolean; text: string }) => (
  <div className={cn(
    'flex items-center gap-1',
    met ? 'text-green-600' : 'text-muted-foreground'
  )}>
    {met ? <Check size={12} /> : <X size={12} />}
    <span>{text}</span>
  </div>
);

export default PasswordStrengthIndicator;
